// Painted foliage, stored as strokes rather than as plants.
//
// The Tilt Brush model: what gets saved is the path you dragged plus the brush
// settings, and the geometry is regenerated from that on load. It matters here
// for a concrete reason — a painted forest is a few thousand plants, and saving
// those individually would be thousands of rows and hundreds of kilobytes per
// world. The stroke that produced them is a few hundred bytes, so a whole
// landscape fits in one row.
//
// It also keeps the work editable. Density, species and scale can be changed
// after the fact, and a single stroke can be deleted without disturbing its
// neighbours — none of which is possible once the plants have been baked out
// into independent objects.
//
// The catch is that regeneration must be deterministic: the same stroke has to
// produce the same plants on every machine and every reload, or a world would
// rearrange itself between visits and no two players would see the same thing.
// Hence the explicit seeded generator below — Math.random() would break this.

import type { FoliageInstance } from './components/FoliageLayer';
import { FOLIAGE_SPECIES, makeTreeCanopy } from './components/FoliageLayer';
import type { TrunkInstance } from './components/TrunkLayer';

export interface FoliageStroke {
  id: string;
  /** Ground-plane path in world space: [x, z] pairs. */
  points: [number, number][];
  /** Brush half-width in metres. */
  radius: number;
  /** Plants per square metre. */
  density: number;
  /** Indices into FOLIAGE_SPECIES that this stroke may place. */
  species: number[];
  scaleMin: number;
  scaleMax: number;
  /** Ground height the stroke was painted at. */
  y: number;
  /** Fixes the layout — same seed, same plants, on every client. */
  seed: number;
  /** Places trunks with canopies instead of ground cover. */
  trees?: boolean;
  /**
   * An erase stroke. Plants nothing; removes plants from earlier strokes whose
   * positions fall within `radius` of this path.
   *
   * Erasing subtracts rather than editing the strokes it overlaps, because the
   * plant layout is generated from the stroke as a whole: trimming points out of
   * a painted stroke would re-run its generator over a shorter path and shuffle
   * every remaining plant in it. Recording the erased region instead leaves
   * untouched plants exactly where they were, keeps the result deterministic,
   * and costs a handful of numbers.
   */
  erase?: boolean;
}

/** Cheap deterministic PRNG. Same sequence everywhere, unlike Math.random. */
function makeRng(seed: number) {
  let s = seed >>> 0 || 1;
  return () => {
    s = (Math.imul(s, 1664525) + 1013904223) >>> 0;
    return s / 4294967296;
  };
}

/** Total length of the painted path, used to size the plant budget. */
function strokeLength(points: [number, number][]): number {
  let d = 0;
  for (let i = 1; i < points.length; i++) {
    d += Math.hypot(points[i][0] - points[i - 1][0], points[i][1] - points[i - 1][1]);
  }
  return d;
}

/** Point at distance `t` along the path, linearly interpolated. */
function pointAt(points: [number, number][], t: number): [number, number] {
  if (points.length === 1) return points[0];
  let acc = 0;
  for (let i = 1; i < points.length; i++) {
    const seg = Math.hypot(points[i][0] - points[i - 1][0], points[i][1] - points[i - 1][1]);
    if (acc + seg >= t || i === points.length - 1) {
      const f = seg > 0 ? (t - acc) / seg : 0;
      return [
        points[i - 1][0] + (points[i][0] - points[i - 1][0]) * f,
        points[i - 1][1] + (points[i][1] - points[i - 1][1]) * f,
      ];
    }
    acc += seg;
  }
  return points[points.length - 1];
}

/** Shortest distance from a point to a polyline, for erase hit-testing. */
function distToPath(x: number, z: number, pts: [number, number][]): number {
  if (pts.length === 1) return Math.hypot(x - pts[0][0], z - pts[0][1]);
  let best = Infinity;
  for (let i = 1; i < pts.length; i++) {
    const [ax, az] = pts[i - 1];
    const [bx, bz] = pts[i];
    const dx = bx - ax;
    const dz = bz - az;
    const len2 = dx * dx + dz * dz;
    const t = len2 > 0 ? Math.max(0, Math.min(1, ((x - ax) * dx + (z - az) * dz) / len2)) : 0;
    best = Math.min(best, Math.hypot(x - (ax + dx * t), z - (az + dz * t)));
  }
  return best;
}

export interface StrokeOutput {
  foliage: FoliageInstance[];
  trunks: TrunkInstance[];
}

/** Rebuild one stroke's plants. Pure and deterministic given the stroke. */
export function instancesFromStroke(stroke: FoliageStroke): StrokeOutput {
  const rnd = makeRng(stroke.seed);
  const foliage: FoliageInstance[] = [];
  const trunks: TrunkInstance[] = [];

  const len = strokeLength(stroke.points);
  // Area of the swept capsule, so a long drag plants proportionally more than a
  // dab and density stays a per-square-metre figure the user can reason about.
  const area = len * stroke.radius * 2 + Math.PI * stroke.radius * stroke.radius;
  const count = Math.max(1, Math.round(area * stroke.density));
  // A hard ceiling per stroke: density is a slider and nothing should let one
  // careless drag allocate a million instances.
  const capped = Math.min(count, stroke.trees ? 120 : 4000);

  const species = stroke.species.length ? stroke.species : [0];

  for (let i = 0; i < capped; i++) {
    const [px, pz] = pointAt(stroke.points, rnd() * len);
    // sqrt keeps the scatter even across the disc instead of bunching at the
    // centre, which a raw uniform radius would do.
    const a = rnd() * Math.PI * 2;
    const r = Math.sqrt(rnd()) * stroke.radius;
    const x = px + Math.cos(a) * r;
    const z = pz + Math.sin(a) * r;
    const scale = stroke.scaleMin + rnd() * (stroke.scaleMax - stroke.scaleMin);

    if (stroke.trees) {
      trunks.push({ position: [x, stroke.y, z], scale, rotation: rnd() * Math.PI * 2 });
      foliage.push(...makeTreeCanopy([x, stroke.y, z], scale, Math.floor(rnd() * 1e9)));
    } else {
      const sp = species[Math.floor(rnd() * species.length)] % FOLIAGE_SPECIES.length;
      foliage.push({
        position: [x, stroke.y, z],
        species: sp,
        scale,
        rotation: rnd() * Math.PI * 2,
      });
    }
  }

  return { foliage, trunks };
}

/**
 * Rebuild every stroke into the two instance lists the renderers consume.
 *
 * An erase stroke only affects strokes painted before it, so painting over an
 * erased patch fills it again rather than being silently swallowed.
 */
export function instancesFromStrokes(strokes: FoliageStroke[]): StrokeOutput {
  const foliage: FoliageInstance[] = [];
  const trunks: TrunkInstance[] = [];

  strokes.forEach((s, i) => {
    if (s.erase) return;
    const later = strokes.slice(i + 1).filter((e) => e.erase);
    const out = instancesFromStroke(s);
    const keep = (p: [number, number, number]) =>
      !later.some((e) => distToPath(p[0], p[2], e.points) <= e.radius);
    out.foliage.forEach((f) => { if (keep(f.position)) foliage.push(f); });
    out.trunks.forEach((t) => { if (keep(t.position)) trunks.push(t); });
  });

  return { foliage, trunks };
}

/**
 * Serialised form. Points are rounded to centimetres before saving — full float
 * precision is meaningless for scattered plants and roughly doubles the row.
 */
export function packStroke(s: FoliageStroke) {
  return {
    ...s,
    points: s.points.map(([x, z]) => [Math.round(x * 100) / 100, Math.round(z * 100) / 100]),
  };
}

export function unpackStroke(raw: any): FoliageStroke | null {
  if (!raw || !Array.isArray(raw.points) || raw.points.length === 0) return null;
  return {
    id: String(raw.id ?? `stroke-${Math.round(raw.seed ?? 0)}`),
    points: raw.points.map((p: any) => [Number(p[0]) || 0, Number(p[1]) || 0]),
    radius: Number(raw.radius) || 1.5,
    density: Number(raw.density) || 1,
    species: Array.isArray(raw.species) ? raw.species.map(Number) : [0],
    scaleMin: Number(raw.scaleMin) || 0.8,
    scaleMax: Number(raw.scaleMax) || 1.2,
    y: Number(raw.y) || 0,
    seed: Number(raw.seed) || 1,
    trees: !!raw.trees,
    erase: !!raw.erase,
  };
}
